import React from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from 'react-query';
import { i18n } from 'i18n';
import { throughputUnitLabelFromThroughputControlType } from 'hooks/useDisplayUnits/display';
import { BasicDisplayUnits, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import { MINE_MODEL_KEY, useBulkUpdateSpoolsMutation } from 'providers/api';
import { sanitiseNumber } from 'utils/form';
import { selectableRheology } from 'utils/rheology';
import { pumpPressureBarToKpa } from 'utils/unitConversions';
import { Model } from '../hooks/useModelState';
import { PCStope } from '../MineModel/Stopes';
import StopeMultiSelect from '../Components/StopeMultiSelect';
import UnitSystemNumberField from '../StopePanelPage/RecipesTab/RecipeForm/UnitSystemNumberField';
import { getBulkEditSceneState, updateBulkEditSceneState } from '../Scenes/BulkEditScene/bulkEditSceneState';
import { splitSelectedIdsByType } from '../Scenes/BulkEditScene/Toolbar/utils';
import AutoPipeAssignerResults from './AutoPipeAssignerResults';
import PipeUsageTable from './PipeUsageTable';
import { autoAssignPipes, isResultStale } from './autoAssignPipes';
import { readRememberedInputs, rememberInputs } from './rememberedInputs';
import {
  SetupInputs,
  curingDayOptions,
  datasetThroughput,
  effectiveThroughput,
  hasUcsData,
  initialSetupInputs,
  validateSetup,
  withRheology
} from './setupInputs';
import { AssignerProgress, AutoPipeAssignerResult, StartFrom } from './types';
import useAssignerUnits from './useAssignerUnits';

interface AutoPipeAssignerDialogProps {
  model: Model;
  onClose: () => void;
}

type Phase = 'setup' | 'running' | 'results' | 'confirm' | 'applying' | 'applied';

const allStopes = (model: Model): PCStope[] => Object.values(model.stopes)
  .flat()
  .filter((stope) => !!stope?.data?.stopeId);

const progressText = (progress: AssignerProgress | undefined): string => {
  if (!progress) return i18n._('Starting...');
  const values = { index: progress.stopeIndex + 1, count: progress.stopeCount, pass: progress.pass };
  return progress.stage === 'routes'
    ? i18n._('Finding routes: stope {index} of {count}', values)
    : i18n._('Solving: stope {index} of {count}, pass {pass}', values);
};

const progressValue = (progress: AssignerProgress | undefined): number => (
  progress && progress.stopeCount > 0 ? (100 * (progress.stopeIndex + 1)) / progress.stopeCount : 0
);

const AutoPipeAssignerDialog = ({ model, onClose }: AutoPipeAssignerDialogProps) => {
  const { project } = model;
  const projectId = project.entityId as string;
  const throughputControlType = project.throughput;
  const pipes = React.useMemo(() => Object.values(model.pipes), [model.pipes]);
  const stopes = React.useMemo(() => allStopes(model), [model.stopes]);
  const rheologies = React.useMemo(() => selectableRheology(model.rheology), [model.rheology]);
  const units = useAssignerUnits(project);
  const queryClient = useQueryClient();
  const bulkUpdateSpools = useBulkUpdateSpoolsMutation(projectId);
  const abortRef = React.useRef<AbortController | null>(null);

  const [inputs, setInputs] = React.useState<SetupInputs>(() => {
    const { stopeIds } = splitSelectedIdsByType(model, getBulkEditSceneState().selectedIds);
    const remembered = readRememberedInputs(projectId, pipes.map(({ pipeId }) => pipeId));
    return initialSetupInputs(project, model.rheology, pipes, remembered, stopes.filter((stope) => stopeIds.includes(stope.data.stopeId)));
  });
  const [phase, setPhase] = React.useState<Phase>('setup');
  const [progress, setProgress] = React.useState<AssignerProgress>();
  const [result, setResult] = React.useState<AutoPipeAssignerResult>();
  const [notice, setNotice] = React.useState<string>();

  const rheology = rheologies.find(({ rheologyDataSetId }) => rheologyDataSetId === inputs.rheologyDataSetId);
  const usesUcs = hasUcsData(rheology);
  const { errors, warnings, settings } = validateSetup(inputs, rheology);
  const isolated = model.isNetworkFiltered;
  const hasPlant = !!model.activeBackfillPlant;
  const canRun = !!settings && !isolated && hasPlant;
  const canApply = !!result && result.converged && result.changes.length > 0;
  const update = (changes: Partial<SetupInputs>) => setInputs((current) => ({ ...current, ...changes }));

  const handleRun = async () => {
    if (!settings || !rheology) return;
    rememberInputs(projectId, { ...settings, usage: inputs.usage });
    const controller = new AbortController();
    abortRef.current = controller;
    setNotice(undefined);
    setProgress(undefined);
    setPhase('running');
    try {
      const planned = await autoAssignPipes({
        model, stopes: inputs.stopes, rheology, settings, usage: inputs.usage, signal: controller.signal, onProgress: setProgress,
      });
      if (!planned) {
        setPhase('setup');
        return;
      }
      setResult(planned);
      setPhase('results');
    } catch {
      setNotice(i18n._('The run stopped with an error. Nothing was changed.'));
      setPhase('setup');
    } finally {
      abortRef.current = null;
    }
  };

  const handleApply = async () => {
    if (!result) return;
    if (isResultStale(model, result.changes)) {
      setNotice(i18n._('Pipes on these routes have changed since the run. Run again before applying.'));
      setPhase('results');
      return;
    }
    setPhase('applying');
    try {
      await bulkUpdateSpools.mutateAsync({
        projectId,
        spools: result.changes.map(({ spoolId, toPipeId }) => ({ spoolId, pipeId: toPipeId })),
      });
      // Joins the reload the mutation started, so the network on screen already shows the new pipes.
      await queryClient.refetchQueries(MINE_MODEL_KEY, { active: true });
      setPhase('applied');
    } catch {
      // The global mutation cache has already reported the failure.
      setPhase('results');
    }
  };

  const handleSelectChanged = () => {
    updateBulkEditSceneState({ entityType: 'Spool', isSelecting: false, selectedIds: result?.changes.map(({ spoolId }) => spoolId) ?? [] });
    onClose();
  };

  const handleClose = () => {
    if (phase === 'applying') return;
    abortRef.current?.abort();
    onClose();
  };

  const handleRunAgain = () => {
    setNotice(undefined);
    setPhase('setup');
  };

  const { pumpPressure } = units;
  const pumpPressureDisplay = inputs.pumpPressureBar !== undefined ? pumpPressure.toDisplay(inputs.pumpPressureBar) : undefined;

  const setupContent = (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography variant="body2" color="text.secondary">
          {i18n._('Solves each stope\'s paste mix at the pump pressure and steps every overpressured spool up to the next higher-rated pipe of its class.')}
          {' '}
          {i18n._('Nothing changes until you apply the preview.')}
        </Typography>
      </Grid>
      {!hasPlant && <Grid size={12}><Alert severity="error">{i18n._('The model has no backfill plant.')}</Alert></Grid>}
      {isolated && (
        <Grid size={12}>
          <Alert severity="warning">{i18n._('Isolation hides part of the network from the route search. Click Show All on the General tab first.')}</Alert>
        </Grid>
      )}
      {notice && <Grid size={12}><Alert severity="error">{notice}</Alert></Grid>}
      <Grid size={12}>
        <StopeMultiSelect stopes={stopes} value={inputs.stopes} onChange={(selected) => update({ stopes: selected })} error={!!errors.stopes} />
        {errors.stopes && <FormHelperText error>{errors.stopes}</FormHelperText>}
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          select
          fullWidth
          size="small"
          color="secondary"
          label={i18n._('Rheology Dataset')}
          value={inputs.rheologyDataSetId ?? ''}
          error={!!errors.rheology}
          helperText={errors.rheology}
          onChange={(event) => {
            const chosen = rheologies.find(({ rheologyDataSetId }) => rheologyDataSetId === event.target.value);
            if (chosen) setInputs((current) => withRheology(current, chosen));
          }}
        >
          {rheologies.map((dataset) => (
            <MenuItem key={dataset.rheologyDataSetId} value={dataset.rheologyDataSetId}>
              {`${dataset.reference} ${dataset.version ?? ''}`.trim()}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <UnitSystemNumberField
          size="small"
          label={i18n._('Pump Pressure')}
          unitSystem={project.unitSystemPreference}
          customUnitFields={project.customUnitFields}
          unitInfo={pumpPressure.unit}
          decimalPlaces={pumpPressure.unit.decimalPlaces}
          value={pumpPressureDisplay}
          onChange={(event) => update({ pumpPressureBar: pumpPressure.fromDisplay(sanitiseNumber(event.target.value) ?? 0) })}
          error={!!errors.pumpPressure}
          helperText={errors.pumpPressure ?? (rheology
            ? i18n._('Dataset default: {value}, maximum: {max}', {
              value: units.formatPumpPressureKpa(pumpPressureBarToKpa(rheology.pumpPressure.default)),
              max: units.formatPumpPressureKpa(pumpPressureBarToKpa(rheology.pumpPressure.max)),
            })
            : undefined)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <UnitSystemNumberField
          size="small"
          label={i18n._('Throughput')}
          unitSystem={project.unitSystemPreference}
          customUnitFields={project.customUnitFields}
          unitInfo={throughputUnitLabelFromThroughputControlType(throughputControlType)}
          decimalPlaces={0}
          value={effectiveThroughput(inputs, rheology)}
          onChange={(event) => update({ throughput: sanitiseNumber(event.target.value) })}
          error={!!errors.throughput}
          helperText={errors.throughput ?? (rheology
            ? i18n._('Dataset default: {value}', { value: units.formatThroughput(datasetThroughput(rheology) ?? 0) })
            : undefined)}
        />
      </Grid>
      {usesUcs && (
        <>
          <Grid size={{ xs: 12, md: 4 }}>
            <UnitSystemNumberField
              size="small"
              label={i18n._('Target UCS')}
              unitSystem={project.unitSystemPreference}
              customUnitFields={project.customUnitFields}
              unitInfo={unitLabels[BasicDisplayUnits.PressureUCS]}
              decimalPlaces={0}
              value={inputs.targetUCS}
              onChange={(event) => update({ targetUCS: sanitiseNumber(event.target.value) })}
              helperText={i18n._('Optional. Leave blank to solve at the minimum binder.')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              select
              fullWidth
              size="small"
              color="secondary"
              label={i18n._('Target Days')}
              disabled={!inputs.targetUCS}
              value={inputs.curingDays ?? ''}
              onChange={(event) => update({ curingDays: Number(event.target.value) })}
            >
              {curingDayOptions(rheology).map((days) => <MenuItem key={days} value={days}>{days}</MenuItem>)}
            </TextField>
          </Grid>
        </>
      )}
      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          size="small"
          type="number"
          color="secondary"
          label={i18n._('Pipe Rating Offset')}
          value={inputs.allowancePercent ?? ''}
          onChange={(event) => update({ allowancePercent: event.target.value === '' ? undefined : Number(event.target.value) })}
          error={!!errors.allowance}
          helperText={errors.allowance ?? i18n._('Percentage of pump pressure added to grade line')}
          slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <Autocomplete
          multiple
          size="small"
          disabled={!inputs.allowancePercent}
          options={pipes.map(({ pipeId }) => pipeId)}
          value={inputs.excludedPipeIds}
          getOptionLabel={(pipeId) => model.pipes[pipeId]?.name ?? pipeId}
          onChange={(_event, excludedPipeIds) => update({ excludedPipeIds })}
          disableCloseOnSelect
          renderInput={(params) => (
            <TextField
              {...params}
              color="secondary"
              label={i18n._('Exclude Pipes from Offset Check')}
              placeholder={i18n._('Select pipes to exclude')}
            />
          )}
        />
      </Grid>
      <Grid size={12}>
        <TextField
          select
          fullWidth
          size="small"
          color="secondary"
          label={i18n._('Start From')}
          value={inputs.startFrom}
          onChange={(event) => update({ startFrom: event.target.value as StartFrom })}
          helperText={inputs.startFrom === 'lowest'
            ? i18n._('Spools on the selected routes start on the lowest-rated pipe of their class and step up as needed, so they can also move down.')
            : i18n._('Only overpressured spools change, and only to a higher-rated pipe.')}
        >
          <MenuItem value="current">{i18n._('Current pipes')}</MenuItem>
          <MenuItem value="lowest">{i18n._('Lowest-rated pipes')}</MenuItem>
        </TextField>
      </Grid>
      <Grid size={12}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>{i18n._('Pipes the tool may use')}</Typography>
        <PipeUsageTable
          pipes={pipes}
          usage={inputs.usage}
          onChange={(pipeId, usage) => setInputs((current) => ({ ...current, usage: { ...current.usage, [pipeId]: usage } }))}
          formatNumber={units.formatNumber}
        />
        {errors.usage && <FormHelperText error>{errors.usage}</FormHelperText>}
        {warnings.map((warning) => <Alert key={warning} severity="warning" sx={{ mt: 1 }}>{warning}</Alert>)}
      </Grid>
    </Grid>
  );

  const resultsContent = result && (
    <>
      {!result.converged && (
        <Alert severity="error" sx={{ mb: 2 }}>{i18n._('The run did not settle, so its changes cannot be applied.')}</Alert>
      )}
      {notice && <Alert severity="error" sx={{ mb: 2 }}>{notice}</Alert>}
      {phase === 'confirm' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {i18n._('{count, plural, one {Apply # pipe change?} other {Apply # pipe changes?}}', { count: result.changes.length })}
          {' '}
          {i18n._('Recipes in this scenario whose routes cross a changed spool will be marked stale and need re-optimising.')}
          {' '}
          {i18n._('Undo restores every pipe in one step.')}
        </Alert>
      )}
      {phase === 'applied' && <Alert severity="success" sx={{ mb: 2 }}>{i18n._('Pipe changes applied.')}</Alert>}
      <AutoPipeAssignerResults result={result} units={units} />
    </>
  );

  const actions = (() => {
    switch (phase) {
      case 'setup':
        return (
          <>
            <Button color="secondary" onClick={handleClose}>{i18n._('Close')}</Button>
            <Button variant="contained" color="secondary" onClick={handleRun} disabled={!canRun}>{i18n._('Run')}</Button>
          </>
        );
      case 'running':
        return <Button color="secondary" onClick={() => abortRef.current?.abort()}>{i18n._('Cancel')}</Button>;
      case 'results':
        return (
          <>
            <Button color="secondary" onClick={handleClose}>{i18n._('Close')}</Button>
            <Button color="secondary" onClick={handleRunAgain}>{i18n._('Run again')}</Button>
            <Button variant="contained" color="secondary" onClick={() => setPhase('confirm')} disabled={!canApply}>{i18n._('Apply changes')}</Button>
          </>
        );
      case 'confirm':
      case 'applying':
        return (
          <>
            <Button color="secondary" onClick={() => setPhase('results')} disabled={phase === 'applying'}>{i18n._('Back')}</Button>
            <Button variant="contained" color="secondary" onClick={handleApply} disabled={phase === 'applying'}>{i18n._('Apply')}</Button>
          </>
        );
      default:
        return (
          <>
            <Button color="secondary" onClick={handleSelectChanged}>{i18n._('Select changed spools')}</Button>
            <Button variant="contained" color="secondary" onClick={handleClose}>{i18n._('Close')}</Button>
          </>
        );
    }
  })();

  React.useEffect(() => () => abortRef.current?.abort(), []);

  return (
    <Dialog open onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>{i18n._('Auto Assign Pipes')}</DialogTitle>
      <DialogContent dividers>
        {phase === 'setup' && setupContent}
        {phase === 'running' && (
          <Box sx={{ py: 4 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>{progressText(progress)}</Typography>
            <LinearProgress color="secondary" variant={progress ? 'determinate' : 'indeterminate'} value={progressValue(progress)} />
          </Box>
        )}
        {phase !== 'setup' && phase !== 'running' && resultsContent}
        {phase === 'applying' && <LinearProgress color="secondary" sx={{ mt: 2 }} />}
      </DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
};

export default AutoPipeAssignerDialog;
